import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Container, ChartWrapper, LegendContainer, LegendGradient, LegendTicks, EmptyState, LegendLabel } from './index.styles';
import { useContainerHeight } from '../useContainerHeight';
import { resolveDayOfWeek, formatHour, formatCompact } from '../../../../helpers';
import ProfileIcons from '../../../ProfileIcons';
import { CHART_GRID_COLOR, CHART_TICK_COLOR, COLOR_STOPS } from '../consts';
import { ChartTooltip } from '../TooltipComponent';

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_INDEX: Record<string, number> = Object.fromEntries(DAYS_ORDER.map((d, i) => [d, i]));
const HOUR_TICKS = Array.from({ length: 24 }, (_, i) => i);
const DAY_TICKS = Array.from({ length: 7 }, (_, i) => i);
const TICK_COLOR = CHART_TICK_COLOR;
const TICK_SIZE = 12;
const RADIUS_MIN = 3;
const RADIUS_MAX = 12;

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

const intensityToColor = (t: number) => {
  const c = clamp01(t);
  const seg = c * (COLOR_STOPS.length - 1);
  const i = Math.min(Math.floor(seg), COLOR_STOPS.length - 2);
  const f = seg - i;
  const [r1, g1, b1] = COLOR_STOPS[i];
  const [r2, g2, b2] = COLOR_STOPS[i + 1];
  const mix = (a: number, b: number) => Math.round(a + (b - a) * f);

  return `rgb(${mix(r1, r2)}, ${mix(g1, g2)}, ${mix(b1, b2)})`;
};

interface HeatPoint {
  x: number;
  y: number;
  value: number;
}

type IndexableDatum = NonNullable<HeatMapModuleData['data']>[number] & Record<string, string | number>;

interface BubbleProps {
  cx?: number;
  cy?: number;
  payload?: HeatPoint;
  min: number;
  max: number;
}

const Bubble = ({ cx, cy, payload, min, max }: BubbleProps) => {
  if (cx == null || cy == null || !payload) return null;

  const t = max > min ? clamp01((payload.value - min) / (max - min)) : 0;
  const r = RADIUS_MIN + (RADIUS_MAX - RADIUS_MIN) * Math.sqrt(t);

  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={intensityToColor(t)}
    />
  );
};

type AxisTickProps = {
  x?: number;
  y?: number;
  payload?: { value: number };
};

const HourTick = ({ x, y, payload }: AxisTickProps) => {
  if (x == null || y == null || !payload) return null;

  return (
    <g transform={`translate(${x}, ${y})`}>
      <text
        transform="rotate(-90)"
        textAnchor="start"
        dy="0.32em"
        fontSize={TICK_SIZE}
        fill={TICK_COLOR}
      >
        {formatHour(payload.value)}
      </text>
    </g>
  );
};

export const HeatMapRenderer = ({ data, selectedProfiles, supportedPlatforms }: DynamicModuleBuilderProps) => {
  const { t } = useTranslation('reports');
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const chartHeight = useContainerHeight(chartWrapperRef as React.RefObject<HTMLDivElement>);

  if (!data || data.chartType !== 'HeatMap') {
    return <EmptyState>{t('new_reports_no_performance_data')}</EmptyState>;
  }

  const moduleData = data as HeatMapModuleData & {
    axis?: {
      x?: { dataKey?: string; type?: string };
      y?: { dataKey?: string; type?: string };
    };
  };

  const { data: heatData, legend, axis } = moduleData;

  if (!heatData || heatData.length === 0) {
    return <EmptyState>{t('new_reports_no_performance_data')}</EmptyState>;
  }

  const rows = heatData as IndexableDatum[];

  const xKey = axis?.x?.dataKey ?? 'hour';
  const yKey = axis?.y?.dataKey ?? 'dayOfWeek';

  const points: HeatPoint[] = rows.map((d) => ({
    x: Number(d[xKey]),
    y: DAY_INDEX[String(d[yKey]).toLowerCase()],
    value: d.value,
  }));

  const min = legend?.min ?? 0;
  const max = legend?.max ?? Math.max(...points.map((p) => p.value), 1);

  const legendTicks = Array.from({ length: 5 }, (_, i) => Math.round(min + ((max - min) * i) / 4));

  return (
    <Container>
      <ChartWrapper ref={chartWrapperRef}>
        <ResponsiveContainer
          width="100%"
          height={chartHeight || '100%'}
        >
          <ScatterChart margin={{ top: 25, right: 0, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={CHART_GRID_COLOR} />
            <XAxis
              type="number"
              dataKey="x"
              orientation="top"
              domain={[-0.5, 23.5]}
              ticks={HOUR_TICKS}
              interval={0}
              tick={<HourTick />}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="number"
              dataKey="y"
              reversed
              domain={[-0.5, 6.5]}
              ticks={DAY_TICKS}
              interval={0}
              tickFormatter={(i: number) => resolveDayOfWeek(DAYS_ORDER[i], t).slice(0, 3)}
              tick={{ fontSize: TICK_SIZE, fill: TICK_COLOR }}
              axisLine={false}
              tickLine={false}
              width={44}
            />
            <Tooltip
              content={
                <ChartTooltip
                  variant="heatmap"
                  payloadFilter={(entry) => entry.dataKey === 'y'}
                  getTitle={(entry) => `${resolveDayOfWeek(DAYS_ORDER[entry.payload.y], t)} · ${formatHour(entry.payload.x)}`}
                  getLabel={(entry) => entry.payload.value}
                  valueFormatter={(v) => new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(v)}
                />
              }
            />
            <Scatter
              data={points}
              shape={
                <Bubble
                  min={min}
                  max={max}
                />
              }
              isAnimationActive={false}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </ChartWrapper>
      <LegendContainer>
        <LegendLabel>Value →</LegendLabel>
        <LegendGradient />
        <LegendTicks>
          {legendTicks.map((v) => (
            <span key={v}>{formatCompact(v)}</span>
          ))}
        </LegendTicks>
      </LegendContainer>
      <ProfileIcons
        isChartType
        selectedProfiles={selectedProfiles}
        supportedPlatforms={supportedPlatforms}
      />
    </Container>
  );
};
